import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode } from "@/lib/api";
import {
  ArrowRight,
  CreditCard,
  XCircle,
  RotateCcw,
  AlertTriangle,
  Info,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro", business: "Business" };

const STATUS_KEYS = { paid: "billing.paid", pending: "billing.pending", failed: "billing.failed", complete: "billing.paid" };

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function Billing() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/history").then((r) => setHistory(r.data.transactions || []));
  }, []);

  const handleCancel = async () => {
    if (!window.confirm(t("billing_v2.confirm_cancel"))) return;
    setBusy(true);
    try {
      await api.post("/payments/cancel");
      await refresh();
      toast.success("Subscription cancellation scheduled");
    } catch (err) {
      toast.error(`Error: ${extractErrorCode(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    setBusy(true);
    try {
      await api.post("/payments/reactivate");
      await refresh();
      toast.success("Subscription reactivated");
    } catch (err) {
      toast.error(`Error: ${extractErrorCode(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const tier = user?.subscription_tier || "free";
  const isPaid = tier !== "free";
  const isCancelling = user?.cancel_at_period_end;
  const periodEnd = user?.current_period_end;
  const days = daysUntil(periodEnd);
  const formattedEnd = periodEnd ? new Date(periodEnd).toLocaleDateString() : null;

  return (
    <div className="px-8 py-10 lg:px-12" data-testid="billing-page">
      <h1 className="font-display text-4xl font-extrabold tracking-tight text-white">
        {t("billing.title")}
      </h1>

      {isCancelling && formattedEnd && (
        <div
          className="mt-6 flex items-start gap-3 rounded-xl border border-orange-500/30 bg-orange-500/10 p-4"
          data-testid="billing-cancelling-banner"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-orange-400" />
          <div className="flex-1 text-sm text-orange-200">
            {t("billing_v2.cancelling_banner", { date: formattedEnd })}
          </div>
        </div>
      )}

      {isPaid && !isCancelling && days !== null && days <= 7 && (
        <div
          className="mt-6 flex items-start gap-3 rounded-xl border border-[#9ACD32]/30 bg-[#9ACD32]/10 p-4"
          data-testid="billing-expiring-banner"
        >
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#9ACD32]" />
          <div className="flex-1 text-sm text-slate-200">
            {t("billing_v2.expiring_soon", { date: formattedEnd })}
          </div>
        </div>
      )}

      <Card className="mt-6 border-slate-800 bg-[#0A0C1F] p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              {t("billing.current_plan")}
            </p>
            <h2 className="mt-2 font-display text-3xl font-extrabold text-white">
              {TIER_LABEL[tier]}
            </h2>
            {periodEnd ? (
              <p className="mt-1 text-sm text-slate-400">
                {t("billing.renewal")} {formattedEnd}
                {days !== null && days >= 0 && (
                  <span className="ml-2 text-xs text-slate-500">({days} days)</span>
                )}
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-500">{t("billing.no_renewal")}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {isPaid && !isCancelling && (
              <Button
                onClick={handleCancel}
                disabled={busy}
                variant="outline"
                className="rounded-full border-red-500/40 text-red-300 hover:bg-red-500/10"
                data-testid="billing-cancel-btn"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />}
                {t("billing_v2.cancel_subscription")}
              </Button>
            )}
            {isPaid && isCancelling && (
              <Button
                onClick={handleReactivate}
                disabled={busy}
                className="rounded-full bg-[#0B0E5C] font-bold text-white hover:bg-[#1A1D7A]"
                data-testid="billing-reactivate-btn"
              >
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                {t("billing_v2.reactivate")}
              </Button>
            )}
            <Button
              asChild
              className="lime-glow rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="billing-change-plan-btn"
            >
              <Link to="/pricing">
                <CreditCard className="mr-2 h-4 w-4" />
                {t("billing.change_plan")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
        <p className="mt-5 text-xs text-slate-500">{t("billing_v2.manual_renewal_note")}</p>
      </Card>

      <section className="mt-10">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
          {t("billing.history")}
        </h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="billing-no-history">{t("billing.no_history")}</p>
        ) : (
          <Card className="overflow-hidden border-slate-800 bg-[#0A0C1F] p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Provider</th>
                  <th className="px-5 py-3">Amount</th>
                  <th className="px-5 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((tx) => (
                  <tr key={tx.payment_id} className="border-b border-slate-800/60" data-testid={`billing-row-${tx.payment_id}`}>
                    <td className="px-5 py-3 text-slate-300">{new Date(tx.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 font-semibold text-white">{TIER_LABEL[tx.plan_id]}</td>
                    <td className="px-5 py-3 capitalize text-slate-400">{tx.provider}</td>
                    <td className="px-5 py-3 font-mono text-slate-300">€{tx.amount.toFixed(2)}</td>
                    <td className="px-5 py-3">
                      <span className={`tier-badge ${tx.payment_status === "paid" || tx.status === "complete" ? "tier-pro" : "tier-free"}`}>
                        {t(STATUS_KEYS[tx.payment_status || tx.status] || "billing.pending")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>
    </div>
  );
}
